import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/api/auth.api';
import { resolveAuthError } from '@/components/auth/authMessages';
import { ErrorAlert, Modal } from '@/components/ds';
import { ChevronsUpDown, Check, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import type { Clinic } from '@/types/auth.types';

interface ClinicSwitcherProps {
  collapsed?: boolean;
  /** Called when the menu (mobile) close button is pressed. */
  onCloseMobileMenu?: () => void;
  /** Opens the list as a bottom sheet, for the phone menu. */
  asSheet?: boolean;
}

export const ClinicLogo = ({
  clinic,
  size = 32,
}: {
  clinic?: Pick<Clinic, 'logo' | 'name'> | null;
  size?: number;
}) => {
  const dimension = { width: size, height: size };
  if (clinic?.logo) {
    return (
      <img
        src={clinic.logo}
        alt={clinic.name}
        style={dimension}
        className="rounded-lg object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div
      style={dimension}
      className="rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0"
    >
      <span className="text-small font-bold text-primary-600 leading-none">
        {(clinic?.name?.trim()?.[0] || 'C').toUpperCase()}
      </span>
    </div>
  );
};

export const ClinicSwitcher = ({
  collapsed,
  onCloseMobileMenu,
  asSheet = false,
}: ClinicSwitcherProps) => {
  // Subscribes this tree to language changes; the labels read i18n directly.
  useTranslation();
  const { clinic: currentClinic, selectClinic } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState<string | null>(null);
  const [error, setError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The sheet renders in a portal, outside this element, and brings its own
    // outside-tap and Escape handling.
    if (asSheet) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [asSheet]);

  const fetchClinics = async () => {
    setIsLoading(true);
    try {
      setClinics(await authApi.getUserClinics());
    } catch (error) {
      console.error('Failed to fetch clinics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Up front rather than on open: someone with one clinic has nothing to
  // switch to, and the row should not offer it.
  useEffect(() => {
    void fetchClinics();
  }, []);

  const handleToggle = () => {
    if (!isOpen && clinics.length === 0) {
      void fetchClinics();
    }
    setError('');
    setIsOpen(!isOpen);
  };

  const handleSelectClinic = async (target: Clinic) => {
    if (target.id === currentClinic?.id) {
      setIsOpen(false);
      return;
    }
    setIsSwitching(target.id);
    setError('');
    try {
      if (await selectClinic(target)) {
        // A full load, not a route change. Every open screen, cached query,
        // socket room and hand-fetched list belongs to the clinic just left,
        // and a route change to the page already open would keep showing them.
        // The row keeps its spinner until the page goes.
        window.location.replace('/dashboard');
        return;
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch clinic:', error);
      setError(i18n.t(resolveAuthError(error, 'clinicSelectFailed')));
    }
    setIsSwitching(null);
  };

  const list = (
    <ClinicList
      isLoading={isLoading}
      clinics={clinics}
      currentId={currentClinic?.id}
      isSwitching={isSwitching}
      error={error}
      onSelect={handleSelectClinic}
      roomy={asSheet}
    />
  );

  // ── Collapsed sidebar: just the clinic avatar, click to open ──
  if (collapsed) {
    return (
      <div
        className="relative flex justify-center border-b border-border-subtle py-3"
        ref={dropdownRef}
      >
        <button
          onClick={handleToggle}
          className="rounded-lg hover:ring-2 hover:ring-primary-200 transition-all cursor-pointer"
          aria-label={i18n.t('shell.switchClinic')}
          title={currentClinic?.name}
        >
          <ClinicLogo clinic={currentClinic} />
        </button>
        {isOpen && (
          <Dropdown className="absolute start-full top-2 ms-2 w-64">
            {list}
          </Dropdown>
        )}
      </div>
    );
  }

  const name = (
    <span className="block font-bold text-body leading-tight tracking-tight text-secondary truncate">
      {currentClinic?.name || i18n.t('shell.selectClinic')}
    </span>
  );

  // ── Expanded sidebar: clinic lockup = brand + switch trigger ──
  return (
    <div
      className="relative border-b border-neutral-200 px-3 py-3"
      ref={dropdownRef}
    >
      <div className="flex items-center gap-1">
        {clinics.length === 1 ? (
          <div className="flex-1 flex items-center gap-[10px] min-w-0 px-2 py-2">
            <ClinicLogo clinic={currentClinic} />
            <span className="flex-1 min-w-0">{name}</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleToggle}
            className="group flex-1 flex items-center gap-[10px] min-w-0 rounded-md px-2 py-2
              hover:bg-neutral-100 active:bg-neutral-100 transition-colors cursor-pointer text-start"
            aria-label={i18n.t('shell.switchClinic')}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
          >
            <ClinicLogo clinic={currentClinic} />
            <span className="flex-1 min-w-0">
              {name}
              <span className="block text-micro text-neutral-400 leading-tight mt-0.5">
                {i18n.t('shell.switchClinic')}
              </span>
            </span>
            <ChevronsUpDown
              className={`w-4 h-4 text-neutral-400 flex-shrink-0 transition-colors group-hover:text-neutral-600 ${
                isOpen ? 'text-neutral-600' : ''
              }`}
            />
          </button>
        )}
        {onCloseMobileMenu && (
          <button
            onClick={onCloseMobileMenu}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-neutral-500
              hover:bg-neutral-100 rounded-md transition-colors flex-shrink-0"
            aria-label={i18n.t('shell.closeMenu')}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {asSheet ? (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          isDismissable={isSwitching === null}
          size="sm"
          title={i18n.t('shell.switchClinic')}
          noPadding
        >
          {list}
        </Modal>
      ) : (
        isOpen && (
          <Dropdown className="absolute left-3 right-3 top-full mt-1">
            <p className="px-4 pt-3 pb-1.5 text-micro font-semibold uppercase tracking-wide text-neutral-400">
              {i18n.t('shell.yourClinics')}
            </p>
            {list}
          </Dropdown>
        )
      )}
    </div>
  );
};

const Dropdown = ({
  className = '',
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={`bg-white rounded-xl shadow-lg border border-neutral-200 z-50 overflow-hidden ${className}`}
  >
    {children}
  </div>
);

interface ClinicListProps {
  isLoading: boolean;
  clinics: Clinic[];
  currentId?: string;
  isSwitching: string | null;
  error: string;
  onSelect: (clinic: Clinic) => void;
  /** Full-height touch rows, for the bottom sheet. */
  roomy: boolean;
}

const ClinicList = ({
  isLoading,
  clinics,
  currentId,
  isSwitching,
  error,
  onSelect,
  roomy,
}: ClinicListProps) => {
  if (clinics.length === 0) {
    return isLoading ? (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    ) : (
      <div className="p-4 text-sm text-neutral-500 text-center">
        {i18n.t('shell.noClinics')}
      </div>
    );
  }
  return (
    <>
      <ErrorAlert message={error} className="m-3" />
      <div
        className={
          roomy
            ? 'divide-y divide-border-subtle overflow-hidden rounded-md'
            : 'py-1 max-h-72 overflow-y-auto scroll-thin'
        }
      >
        {clinics.map((clinic) => {
          const isCurrent = clinic.id === currentId;
          return (
            <button
              key={clinic.id}
              type="button"
              onClick={() => onSelect(clinic)}
              disabled={isSwitching !== null}
              aria-current={isCurrent ? 'true' : undefined}
              className={`w-full flex items-center gap-3 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                ${roomy ? 'min-h-16 px-5 py-3' : 'px-3 py-2.5'}
                ${isCurrent ? 'bg-primary-50' : 'hover:bg-neutral-50 active:bg-neutral-100'}`}
            >
              <ClinicLogo clinic={clinic} size={roomy ? 40 : 32} />
              <span
                className={`flex-1 min-w-0 text-start truncate ${roomy ? 'text-body' : 'text-sm'} ${
                  isCurrent
                    ? 'font-semibold text-primary-700'
                    : 'text-neutral-900'
                }`}
              >
                {clinic.name}
              </span>
              {isSwitching === clinic.id ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary-500 flex-shrink-0" />
              ) : isCurrent ? (
                <Check className="w-4 h-4 text-primary-500 flex-shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
    </>
  );
};
